import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@hooks/theme';
import {
  Container,
  ButtonText,
  IconX,
  IconLoading,
  Quantity,
  IconChevronDown,
} from './styles';
import { ButtonProps } from './interfaces';
import { renderIcon } from './renderIcon';

const Button: React.FC<ButtonProps> = ({
  callback,
  type,
  style,
  styleTitle,
  text,
  icon,
  disabled,
  isLoading,
  manualIcon,
  isExpansible,
  quantity,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const getLoadingIconColor = useCallback(() => {
    switch (type) {
      case 'DROPDOWN':
      case 'PRIMARY_BACKGROUND':
        return colors.lighterText;
      case 'GREY_BACKGROUND':
        return colors.primary;
      case 'ITEM_LIST':
        return colors.darkText;
      default:
        return 'transparent';
    }
  }, [type, colors]);

  const handleRenderIcon = useCallback(() => {
    let iconColor = colors.darkText;
    if (type === 'PRIMARY_BACKGROUND' || type === 'DROPDOWN') {
      iconColor = colors.lighterText;
    }
    return renderIcon(icon, iconColor, 15);
  }, [colors, type, icon]);

  const renderContent = useCallback(() => {
    const typeText = t(`buttons.${type.valueOf().toLowerCase()}`);
    switch (type) {
      case 'ITEM_LIST':
        return (
          <Container
            style={{ whiteSpace: 'nowrap', ...style }}
            buttonType={type}
          >
            <ButtonText
              style={{ ...styleTitle, marginRight: 4 }}
              buttonType={type}
            >
              {text ? t(text) : typeText}
            </ButtonText>
            {isLoading ? (
              <IconLoading size={3} color={getLoadingIconColor()} loading />
            ) : (
              <IconX color="#333" onClick={callback} />
            )}
          </Container>
        );
      case 'DROPDOWN':
        return (
          <Container
            style={{ whiteSpace: 'nowrap', ...style }}
            buttonType={type}
          >
            <ButtonText
              style={{ ...styleTitle, marginRight: 4 }}
              buttonType={type}
            >
              {text ? t(text) : typeText}
            </ButtonText>
            <IconChevronDown color="#fff" />
          </Container>
        );
      default:
        return (
          <Container
            style={style}
            buttonType={type}
            onClick={(e) => (!isLoading && callback ? callback(e) : null)}
            disabled={disabled}
            isExpansible={isExpansible}
          >
            {isLoading ? (
              <IconLoading size={3} color={getLoadingIconColor()} loading />
            ) : (
              <>
                {manualIcon || handleRenderIcon()}
                <ButtonText
                  style={styleTitle}
                  buttonType={type}
                  className="visible-on-hover"
                >
                  {text ? t(text) : typeText}
                </ButtonText>
              </>
            )}
            {quantity && <Quantity className="quantity">({quantity})</Quantity>}
          </Container>
        );
    }
  }, [
    type,
    text,
    styleTitle,
    style,
    disabled,
    isLoading,
    callback,
    getLoadingIconColor,
    t,
    manualIcon,
    isExpansible,
    quantity,
    handleRenderIcon,
  ]);

  return renderContent();
};

export default Button;
